import { memo } from 'react';

import { faExternalLink } from '@fortawesome/pro-solid-svg-icons';
import styled from 'styled-components';

import { Button, ButtonVariants, ComponentSizes, Icon } from 'core/ui';

import compumedLogo from './compumed_logo_blue.webp';

export interface ShareTopHeaderProps {
  /** Whether to show the backup viewer button */
  showBackupViewer?: boolean;
  /** Current viewer type being displayed */
  currentViewerType?: 'primary' | 'secondary' | 'tertiary';
  /** Handler for switching to backup viewer */
  onSwitchViewer?: () => void;
  /** Optional additional actions to show in the header */
  actions?: React.ReactNode;
}

export const ShareTopHeader = memo<ShareTopHeaderProps>(({ showBackupViewer = false, currentViewerType = 'primary', onSwitchViewer, actions }) => {
  const getNextViewerLabel = () => {
    switch (currentViewerType) {
      case 'primary':
        return 'Backup Viewer';
      case 'secondary':
        return 'Tertiary Viewer';
      case 'tertiary':
        return 'Primary Viewer';
      default:
        return 'Switch Viewer';
    }
  };

  return (
    <StyledTopHeader>
      <StyledLogoContainer>
        <StyledLogo src={compumedLogo} alt="CompuMed" />
      </StyledLogoContainer>

      <StyledActionsContainer>
        {actions}
        {showBackupViewer && onSwitchViewer && (
          <StyledBackupViewerButton>
            <Button
              variant={ButtonVariants.SECONDARY}
              size={ComponentSizes.SMALL}
              onClick={onSwitchViewer}
              title={`Switch to ${getNextViewerLabel().toLowerCase()}`}
            >
              <Icon icon={faExternalLink} />
              {getNextViewerLabel()}
            </Button>
          </StyledBackupViewerButton>
        )}
      </StyledActionsContainer>
    </StyledTopHeader>
  );
});

ShareTopHeader.displayName = 'ShareTopHeader';

const StyledTopHeader = styled.div`
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.palette.white};
  padding: ${({ theme }) => theme.space.spacing20} ${({ theme }) => theme.space.spacing30};
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.space.spacing20};
  }
`;

const StyledLogoContainer = styled.div`
  display: flex;
  align-items: center;
`;

const StyledLogo = styled.img`
  height: 24px;
  width: auto;

  @media (max-width: 480px) {
    height: 20px;
  }
`;

const StyledActionsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.spacing10};
`;

const StyledBackupViewerButton = styled.div`
  display: flex;
  align-items: center;

  button {
    color: ${({ theme }) => theme.colors.palette.white};
    border-color: ${({ theme }) => theme.colors.palette.white};
    background: transparent;

    &:hover {
      background: rgba(255, 255, 255, 0.1);
      border-color: ${({ theme }) => theme.colors.palette.white};
      color: ${({ theme }) => theme.colors.palette.white};
    }
  }

  @media (max-width: 768px) {
    .icon-container + span {
      display: none; /* Hide text on mobile, show only icon */
    }
  }
`;
